<template>
    <div class="mt-4 mb-3">
        <template v-if="!isStarted">
            <div class="alert alert-brand">
                Öppna fliken "Hämta med BankID" för att starta kontohämtning.
            </div>
        </template>
        <template v-else-if="failed">
            <div class="alert alert-brand">
                Oooops! Vi kunde inte hämta kontouppgifterna just nu. Vänligen
                försök igen eller fyll i dem manuellt.
            </div>
            <div class="text-center">
                <a href="#" @click.prevent="switchToManual">Fyll i manuellt!</a>
                <a class="mt-3 d-block" href="#" @click.prevent="resetState"
                    ><i class="fal fa-rotate-right"></i> Starta om</a
                >
            </div>
        </template>

        <template v-else>
            <div
                v-if="isStarted"
                class="text-center d-flex flex-column justify-content-center"
            >
                <div v-if="awaitingReport" class="alert alert-brand text-left">
                    Tack! Vi hämtar nu dina kontouppgifter, det tar bara någon
                    sekund…
                </div>
                <div v-else class="alert alert-brand text-left">
                    Nedan väljer du vilken bank du har och legitimerar dig mot
                    denna med BankID. När processen är klar visas dina konton
                    här och du kan välja vilket du vill använda för autogiro.
                </div>
                <div
                    v-if="showLoader"
                    class="d-flex justify-content-center align-items-center flex-column py-4"
                >
                    <i
                        class="fa-solid fa-circle-notch fa-spin fa-2x"
                        style="color: var(--primary-color)"
                    ></i>
                </div>
                <div
                    :id="containerId"
                    :class="{
                        'brite-payment-hidden': !isWidgetReady,
                        'brite-payment-visible': isWidgetReady,
                    }"
                    class="brite-payment"
                ></div>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
.brite-payment {
    width: 100%;
    max-width: 100%;
    opacity: 0;
    overflow-x: hidden;
    transition: opacity 0.2s ease-in-out;
}

.brite-payment > div,
.brite-payment iframe {
    max-width: 100%;
}

.brite-payment iframe {
    width: 100%;
    border: 0;
}

.brite-payment-hidden {
    position: absolute;
    left: -9999px;
    top: 0;
    width: 100%;
    height: 600px;
    overflow: hidden;
}

.brite-payment-visible {
    position: static;
    left: auto;
    width: 100%;
    height: 600px;
    overflow-x: hidden;
    overflow-y: visible;
    opacity: 1;
}
</style>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, toRefs, watch } from "vue";
import axios from "axios";
import { v4 as uuid } from "uuid";
import { useActionLog } from "../../composables/useActionLog";
const DEFAULT_BRITE_SDK_URL = "https://websdk.britepayments.io/v1/client.js";
const BRITE_SDK_URL = import.meta.env.VITE_BRITE_SDK_SCRIPT_URL || DEFAULT_BRITE_SDK_URL;
const BRITE_WIDGET_HEIGHT = "600px";
const BRITE_SESSION_STATE = Object.freeze({
  AUTHENTICATION_STARTED: 1,
  AUTHENTICATION_COMPLETED: 2,
  BANK_ACCOUNT_SELECTION_COMPLETED: 4
});
const REPORTED_BRITE_SESSION_STATES = Object.freeze(Object.values(BRITE_SESSION_STATE));
const props = defineProps({
  quoteKey: String,
  isActive: {
    type: Boolean,
    default: false
  },
  restartToken: {
    type: Number,
    default: 0
  }
});
const {
  quoteKey,
  isActive,
  restartToken
} = toRefs(props);
const emit = defineEmits(["success", "switchToManual"]);
const $actionLog = useActionLog();
const id = ref(uuid());
const requestUuid = ref(null);
const isStarted = ref(false);
const isLoading = ref(false);
const awaitingReport = ref(false);
const failed = ref(false);
const pollInterval = ref(null);
const briteClient = ref(null);
const isStarting = ref(false);
const isWidgetReady = ref(false);
const widgetReadyCheckInterval = ref(null);
const forceNewOnNextStart = ref(false);
const startRequestSequence = ref(0);
const reportingSessionStates = ref(new Set());
const reportedSessionStates = ref(new Set());
const containerId = computed(() => {
  return `brite-payment-${id.value}`;
});
const showLoader = computed(() => {
  return isLoading.value || awaitingReport.value || !isWidgetReady.value;
});
async function ensureStarted() {
  if (isStarted.value || isStarting.value || failed.value) {
    return;
  }
  const forceNew = forceNewOnNextStart.value;
  forceNewOnNextStart.value = false;
  await start(forceNew);
}
async function start(forceNew = false) {
  const requestSequence = ++startRequestSequence.value;
  clearPolling();
  stopClient();
  isStarting.value = true;
  isStarted.value = true;
  isLoading.value = true;
  awaitingReport.value = false;
  failed.value = false;
  isWidgetReady.value = false;
  reportingSessionStates.value.clear();
  reportedSessionStates.value.clear();
  await nextTick();
  try {
    const response = await axios.post(route("quotes.brite.accountsReport.request", {
      key: quoteKey.value
    }), {
      force_new: forceNew
    });
    if (requestSequence !== startRequestSequence.value) {
      return;
    }
    requestUuid.value = response.data.request_uuid;
    if (response.data.status === "report_generated" && response.data.account?.number) {
      teardownSessionState();
      $actionLog.logBriteAISAccountsRetrieved(quoteKey.value, "Kontonummer hämtat och visas för mottagaren (från återanvänd session)");
      emit("success", response.data.account);
      return;
    }
    if (response.data.status === "session_completed") {
      awaitingReport.value = true;
    }
    if (response.data.should_start_widget && response.data.token) {
      await loadBriteSdk();
      await nextTick();
      startClient(response.data.token);
    }
    clearPolling();
    pollInterval.value = setInterval(pollStatus, 2000);
  } catch (error) {
    if (requestSequence !== startRequestSequence.value) {
      return;
    }
    console.warn(error);
    failed.value = true;
  } finally {
    if (requestSequence !== startRequestSequence.value) {
      return;
    }
    isLoading.value = false;
    isStarting.value = false;
  }
}
async function loadBriteSdk() {
  if (window.Brite) {
    return;
  }
  const scriptId = "brite-sdk-script";
  const existing = document.getElementById(scriptId);
  if (existing) {
    await new Promise((resolve, reject) => {
      existing.addEventListener("load", resolve, {
        once: true
      });
      existing.addEventListener("error", reject, {
        once: true
      });
    });
    return;
  }
  await new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = BRITE_SDK_URL;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}
function getClientDimensions() {
  return {
    width: "100%",
    height: BRITE_WIDGET_HEIGHT
  };
}
function syncActiveClientDimensions() {
  const dimensions = getClientDimensions();
  const mountPoint = document.querySelector(`#${containerId.value}`);
  if (!mountPoint) {
    return;
  }
  mountPoint.style.width = dimensions.width;
  mountPoint.style.maxWidth = dimensions.width;
  mountPoint.style.height = dimensions.height;
  mountPoint.style.overflowX = "hidden";
  const widgetContainer = mountPoint.firstElementChild;
  if (widgetContainer instanceof HTMLElement) {
    widgetContainer.style.width = dimensions.width;
    widgetContainer.style.maxWidth = dimensions.width;
    widgetContainer.style.height = dimensions.height;
    widgetContainer.style.overflowX = "hidden";
  }
  const iframe = mountPoint.querySelector("iframe");
  if (iframe instanceof HTMLIFrameElement) {
    iframe.style.width = dimensions.width;
    iframe.style.maxWidth = dimensions.width;
    iframe.style.height = dimensions.height;
  }
}
function startClient(token) {
  if (!token || !window.Brite) {
    throw new Error("Brite token or SDK missing");
  }
  const dimensions = getClientDimensions();
  // Logged here rather than when the iframe turns up in the container: Brite's
  // own render can lag far enough behind that the customer has already moved
  // on, and this is the moment we have done our part.
  $actionLog.logBriteAISModuleRendered(quoteKey.value);
  briteClient.value = new window.Brite(token);
  briteClient.value.start({
    selector: `#${containerId.value}`,
    width: dimensions.width,
    height: dimensions.height
  }, state => {
    isWidgetReady.value = true;
    syncActiveClientDimensions();
    handleSessionStateChange(state);
  }, () => {
    isWidgetReady.value = false;
    stopClient();
  });
  syncActiveClientDimensions();
  startWidgetReadyCheck();
}
function handleSessionStateChange(state) {
  const sessionState = Number(state?.session_state ?? state?.state ?? state);
  if (!REPORTED_BRITE_SESSION_STATES.includes(sessionState)) {
    return;
  }
  if (sessionState === BRITE_SESSION_STATE.AUTHENTICATION_STARTED) {
    reportSessionStateWithKeepalive(sessionState);
    return;
  }
  reportSessionState(sessionState);
}
// State 1 fires as the customer is handed to BankID, so the tab is about to be
// backgrounded — on iOS it is the last thing we ever hear from it. A keepalive
// request leaves the device regardless; the retry ladder below would only queue
// timers a suspended page never runs. Fire-and-forget is the right trade: the
// backend falls back to arming on state 2 when a state-1 report is lost.
function reportSessionStateWithKeepalive(sessionState) {
  const activeRequestUuid = requestUuid.value;
  if (!activeRequestUuid || reportedSessionStates.value.has(sessionState)) {
    return;
  }
  reportedSessionStates.value.add(sessionState);
  const url = route("quotes.brite.accountsReport.storeSessionState", {
    key: quoteKey.value,
    requestUuid: activeRequestUuid
  });
  const body = JSON.stringify({
    session_state: sessionState
  });
  if (navigator.sendBeacon?.(url, new Blob([body], {
    type: "application/json"
  }))) {
    return;
  }
  fetch(url, {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body
  }).catch(error => {
    console.warn(error);
  });
}
async function reportSessionState(sessionState) {
  const activeRequestUuid = requestUuid.value;
  if (!activeRequestUuid || reportingSessionStates.value.has(sessionState) || reportedSessionStates.value.has(sessionState)) {
    return;
  }
  reportingSessionStates.value.add(sessionState);
  const retryDelays = [0, 500, 1500];
  for (const delay of retryDelays) {
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    try {
      await axios.post(route("quotes.brite.accountsReport.storeSessionState", {
        key: quoteKey.value,
        requestUuid: activeRequestUuid
      }), {
        session_state: sessionState
      });
      reportedSessionStates.value.add(sessionState);
      break;
    } catch (error) {
      const status = error.response?.status;
      const isTransient = !status || status === 429 || status >= 500;
      if (!isTransient) {
        break;
      }
    }
  }
  reportingSessionStates.value.delete(sessionState);
}
function stopClient() {
  if (briteClient.value && briteClient.value.stop) {
    briteClient.value.stop();
  }
  briteClient.value = null;
  clearWidgetReadyCheck();
}
function startWidgetReadyCheck() {
  clearWidgetReadyCheck();
  let attempts = 0;
  widgetReadyCheckInterval.value = setInterval(() => {
    attempts += 1;
    const container = document.querySelector(`#${containerId.value}`);
    const iframe = container?.querySelector("iframe");
    if (iframe) {
      isWidgetReady.value = true;
      syncActiveClientDimensions();
      clearWidgetReadyCheck();
      return;
    }
    if (attempts >= 100) {
      clearWidgetReadyCheck();
    }
  }, 200);
}
function clearWidgetReadyCheck() {
  if (widgetReadyCheckInterval.value) {
    clearInterval(widgetReadyCheckInterval.value);
  }
  widgetReadyCheckInterval.value = null;
}
function clearPolling() {
  if (pollInterval.value) {
    clearInterval(pollInterval.value);
  }
  pollInterval.value = null;
}
async function pollStatus() {
  if (!requestUuid.value || failed.value) {
    return;
  }
  try {
    const response = await axios.get(route("quotes.brite.accountsReport.getRequest", {
      key: quoteKey.value,
      requestUuid: requestUuid.value
    }));
    if (response.data.status === "report_generated") {
      if (!response.data.account?.number) {
        return;
      }
      teardownSessionState();
      $actionLog.logBriteAISAccountsRetrieved(quoteKey.value, "Kontonummer hämtat och visas för mottagaren");
      emit("success", response.data.account);
      return;
    }
    if (response.data.status === "session_completed" && !awaitingReport.value) {
      awaitingReport.value = true;
      stopClient();
    }
    if (["session_aborted", "session_failed", "report_failed"].includes(response.data.status)) {
      failed.value = true;
      clearPolling();
      stopClient();
    }
  } catch (error) {
    console.warn(error);
  }
}
function switchToManual() {
  teardownSessionState();
  emit("switchToManual");
}
async function resetState() {
  teardownSessionState();
  failed.value = false;
  forceNewOnNextStart.value = false;
  if (isActive.value) {
    await start(true);
  }
}
function teardownSessionState() {
  startRequestSequence.value += 1;
  clearPolling();
  stopClient();
  requestUuid.value = null;
  isStarted.value = false;
  isLoading.value = false;
  isStarting.value = false;
  awaitingReport.value = false;
  isWidgetReady.value = false;
}
watch(isActive, async value => {
  if (!value) {
    return;
  }
  await ensureStarted();
}, {
  immediate: true
});
watch(restartToken, async (value, previousValue) => {
  if (value === previousValue) {
    return;
  }
  forceNewOnNextStart.value = true;
  if (isActive.value) {
    await resetState();
  }
});
onBeforeUnmount(() => {
  clearPolling();
  stopClient();
  clearWidgetReadyCheck();
});
</script>
